import {
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  LOCK_WARNING,
  ListContext,
  ListContextValue,
  LockedIcon,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  deepCopy,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { ContributorMatch, DataModelEnum, Permission, PermissionAction } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const ContributorMatchPageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  match: ContributorMatch;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
  canSendUnallowableEmail?: boolean;
  willSendUnallowableEmail?: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.match.id;

  const save = (match: ContributorMatch) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.contributorMatches
        .saveUpdate(match)
        .then((response) => {
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
          props.reroute(id, "view");
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    } // if creating new: we need to check if we are creating multiple with dates, or just creating single
    else {
      if (match?.isCreatingBulk) {
        let savingContributorMatch = deepCopy(match);
        savingContributorMatch.date = new Date(); // placeholder date: since date is required in the model but we are not actually using it here
        ApiService.contributorMatches
          .saveCreateBulk(savingContributorMatch)
          .then(() => {
            navigate("../");
            listContext.loadList?.();
            dispatch(setAppToast(newToast("success")));
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            props.setValidationErrors(ex.response);
          });
      } else {
        ApiService.contributorMatches
          .saveCreate(match)
          .then((response) => {
            const id = response;
            props.reroute(id, "view");
            props.fetch(id);
            listContext.loadList?.();
            dispatch(setAppToast(newToast("success")));
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            props.setValidationErrors(ex.response);
          });
      }
    }

    if (props.canSendUnallowableEmail && props.willSendUnallowableEmail) {
      sendUnallowableEmail(match?.id);
    }
  };

  const sendUnallowableEmail = (id: number | undefined) => {
    ApiService.contributorMatches.sendUnallowableEmail(id).then((response) => {
      dispatch(setAppToast(newToast("success", "Email sent succesfully")));
    });
  };

  const toggleLock = (id: number) => {
    if (window.confirm(LOCK_WARNING)) {
      ApiService.contributorMatches.toggleLock(id).then((response) => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.contributorMatches.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (isAuthorized(props.rootPermission, PermissionAction.Lock)) {
      if (!props.match.isLocked)
        menuItems.push({
          label: <Typography color="error">Lock</Typography>,
          action: () => toggleLock(id),
        });
      else
        menuItems.push({
          label: "Unlock",
          action: () => toggleLock(id),
        });
    }

    if (
      isAuthorized(props.rootPermission, PermissionAction.Delete) &&
      (!props.match.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock))
    )
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">
          {id === 0 ? "New" : ""} {props.handle}
        </Typography>

        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.match.isLocked && <LockedIcon />}

        {id > 0 && <DataModelInformation color={"primary"} dataModelType={DataModelEnum.ContributorMatch} dataModelId={id} />}

        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "view" &&
          isAuthorized(props.rootPermission, PermissionAction.Update) &&
          !props.match.isBeforeMatchStaticEndDate &&
          (!props.match.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
            <UpdateButton onClick={() => props.reroute(id, "edit")} />
          )}

        {props.crudMode === "edit" && (
          <SubmitButton onClick={() => save(props.match)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
